import type {SidebarItem} from "./sidebar";
import React, { FC } from "react";
import { Links, useLocation, useNavigate } from "react-router";
import {
  User,
  Badge,
  Avatar,
  Button,
  ScrollShadow,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  Spacer,
} from "@heroui/react";
import {Icon} from "@iconify/react";

import {
  AcmeIcon,
  GridIcon,
  AccountIcon,
  Cart,
  Bill,
} from "./acme";

import NotificationsCard from "./notifications-card";
import Sidebar from "./sidebar";
import SidebarChip from "./sidebar-chip";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslations } from "../../contexts/translation.context";
import Typography from "../typography/Typography";
import { Link } from "react-router";
import { LogoCreatorIc } from "../icons";

// Мемоизированные иконки для предотвращения ререндеров
const DashboardIcon = React.memo(() => {
  const { t } = useTranslations();
  return (
    <GridIcon className="text-default-400 group-data-[selected=true]:text-white" aria-label={t('aria.dashboardIcon')}/>
  );
});
DashboardIcon.displayName = "DashboardIcon";

const AccountsIcon = React.memo(() => {
  const { t } = useTranslations();
  return (
    <AccountIcon className="text-default-400 group-data-[selected=true]:text-white" aria-label={t('aria.accountsIcon')}/>
  );
});
AccountsIcon.displayName = "AccountsIcon";

const AccountsChip = React.memo(() => {
  return (
    <SidebarChip ariaLabel={`Количество аккаунтов: 2`}>
      2
    </SidebarChip>
  );
});
AccountsChip.displayName = "AccountsChip";

// Мемоизированные компоненты для Offers секции
const BillIcon = React.memo(() => {
  const { t } = useTranslations();
  return (
    <Bill className="text-default-400 group-data-[selected=true]:text-white" aria-label={t('aria.billsIcon')}/>
  );
});
BillIcon.displayName = "BillIcon";

const OffersChip = React.memo(() => {
  const {proposalsLength} = useAuth()
  return(
    <SidebarChip ariaLabel={`Количество офферов: ${proposalsLength}`}>
      {proposalsLength}
    </SidebarChip>
  )
});
OffersChip.displayName = "OffersChip";

const CampaignsChip = React.memo(() => {
  const { t } = useTranslations();
  return (
    <SidebarChip ariaLabel={`${t('aria.campaignsCount')}: 6`}>
      6
    </SidebarChip>
  );
});
CampaignsChip.displayName = "CampaignsChip";

const BillsChip = React.memo(() => {
  const { t } = useTranslations();
  return (
    <SidebarChip ariaLabel={`${t('aria.billsCount')}: 6`}>
      6
    </SidebarChip>
  );
});
BillsChip.displayName = "BillsChip";

// Мемоизированные компоненты для подписчиков
const ProposalsChip = React.memo(() => {
  const {proposalsLength} = useAuth()
  return(
    <SidebarChip ariaLabel={`Количество предложений: ${proposalsLength}`}>
      {proposalsLength}
    </SidebarChip>
  )
});
ProposalsChip.displayName = "ProposalsChip";

const OrdersIcon = React.memo(() => {
  const { t } = useTranslations();
  return (
    <Cart className="text-default-400 group-data-[selected=true]:text-white" aria-label={t('aria.ordersIcon')}/>
  );
});
OrdersIcon.displayName = "OrdersIcon";

const OrdersChip = React.memo(() => {
  const {ordersLength, ordersActiveLength} = useAuth();
  const { t } = useTranslations();
  return(
    <SidebarChip ariaLabel={`${t('aria.ordersCount')}: ${ordersActiveLength} из ${ordersLength}`}>
      {ordersActiveLength} / {ordersLength}
    </SidebarChip>
  );
});
OrdersChip.displayName = "OrdersChip";
const WalletChip = React.memo(() => {
  const {user} = useAuth()
  return(
  <SidebarChip ariaLabel={`Баланс: $${user?.balance?.toFixed(2) || '0.00'}`}>
    ${user?.balance?.toFixed(2) || '0.00'}
  </SidebarChip>
)});
// Функция для создания сайдбар элементов с переводами
const createSidebarItems = (t: (key: string) => string): SidebarItem[] => [
  {
    key: "home",
    href: "/client/dashboard",
    startContent: <DashboardIcon />,
    title: t('sidebar.dashboard'),
  },
  {
    key: "accounts",
    href: "/client/accounts",
    startContent: <AccountsIcon />,
    title: t('sidebar.accounts'),
    endContent: <AccountsChip />,
  },
  {
    key: "proposals",
    title: `${t('sidebar.subscribers')} (CPL)`,
    items:[
        {
          key: "proposals",
          href: "/client/subscribers/proposals",
          icon: "solar:checklist-minimalistic-outline",
          title: t('sidebar.proposals'),
          endContent: <ProposalsChip />,
        },
        {
          key: "orders",
          href: "/client/subscribers/orders",
          startContent: <OrdersIcon />,
          title: t('sidebar.orders'),
          endContent: <OrdersChip />,
        },
    ]
  },
  // {
  //   key: "offers",
  //   title: "Офферы (Revshare)",
  //   items:[
  //       {
  //         key: "offers-list",
  //         href: "/client/offers/list",
  //         icon: "solar:bill-list-outline",
  //         title: "Офферы",
  //         endContent: <OffersChip />,
  //       },
  //       {
  //         key: "campaigns",
  //         href: "/client/offers/campaigns",
  //         icon: "solar:star-line-duotone",
  //         title: "Кампании",
  //         endContent: <CampaignsChip />,
  //       },
  //       {
  //         key: "bills",
  //         href: "/client/offers/bills",
  //         startContent: <BillIcon />,
  //         title: "Счета",
  //         endContent: <BillsChip />,
  //       },
  //   ]
  // },
  {
    key: "stats",
    title: t('sidebar.statistics'),
    items:[
        {
          key: "statistics",
          href: "/client/statistics",
          icon: "solar:pie-chart-2-outline",
          title: t('sidebar.statistics'),
        },
        {
          key: "subscribers",
          href: "/client/statistics/subscribers",
          icon: "solar:users-group-two-rounded-outline",
          title: t('sidebar.subscribers'),
        },
        {
          key: "transactions",
          href: "/client/statistics/transactions",
          icon: "solar:card-transfer-outline",
          title: t('sidebar.transactions'),
        },
    ]
  },
  {
    key: "account",
    title: t('sidebar.account'),
    items:[
        {
          key: "wallet",
          href: "/client/account/wallet",
          icon: "solar:wallet-linear",
          title: t('sidebar.wallet'),
          endContent: <WalletChip />,
        },
        {
          key: "settings",
          href: "/client/account/settings",
          icon: "solar:settings-outline",
          title: t('sidebar.settings'),
        },
    ]
  },
];


const SidebarActiveTracker = React.memo(() => {
  const location = useLocation();
  const deferredPathname = React.useDeferredValue(location.pathname);
  const { t } = useTranslations();

  const sidebarItems = React.useMemo(() => createSidebarItems(t), [t]);

  const selectedKey = React.useMemo(() => {
    let bestMatch = { key: "home", matchLength: 0 };

    for (const item of sidebarItems) {
      if (item.href) {
        if (deferredPathname === item.href) {
          return item.key;
        } else if (deferredPathname.startsWith(item.href + "/")) {
          if (item.href.length > bestMatch.matchLength) {
            bestMatch = { key: item.key, matchLength: item.href.length };
          }
        }
      }
      if (item.items) {
        for (const subItem of item.items) {
          if (subItem.href) {
            if (deferredPathname === subItem.href) {
              return subItem.key;
            } else if (deferredPathname.startsWith(subItem.href + "/")) {
              if (subItem.href.length > bestMatch.matchLength) {
                bestMatch = { key: subItem.key, matchLength: subItem.href.length };
              }
            }
          }
        }
      }
    }

    return bestMatch.key;
  }, [deferredPathname, sidebarItems]);

  return <SidebarContent selectedKey={selectedKey} sidebarItems={sidebarItems} />;
});

const SidebarContent = React.memo<{selectedKey: string, sidebarItems: SidebarItem[]}>(({ selectedKey, sidebarItems }) => {
  const [isPending, startTransition] = React.useTransition();
  const { user } = useAuth();

  const sidebarProps = React.useMemo(() => ({
    iconClassName: "",
    items: sidebarItems,
    selectedKey,
    isPending,
    startTransition,
    userRole: user?.role,
  }), [selectedKey, isPending, user?.role, sidebarItems]);

  return <Sidebar {...sidebarProps} />;
});

SidebarContent.displayName = "SidebarContent";
const getInitials = (name:any) => {
  if (!name) return '';
  
  const words = name.trim().split(' ');
  
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  } else if (words.length >= 2) {
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  }
  
  return '';
};

// Изолированный компонент хедера
const SidebarHeader = React.memo(() => {
  const {logout} = useAuth();
  const navigate = useNavigate();
  const {user, notifications} = useAuth()
  const { t } = useTranslations();
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-[8px] px-2">
        <Link to="/client/dashboard" >
          <LogoCreatorIc />
        </Link>
        <span className="text-[12px] font-regular text-[#71717A] select-none">{user?.type === "creator" ? t('sidebar.creator') : user?.type === "agency" ? t('sidebar.agency') : t('sidebar.marketer')}</span>
      </div>
      <div className="flex items-center justify-end">
        <Popover offset={12} placement="bottom-start" aria-label={t('aria.notificationsPanel')}>
          <PopoverTrigger>
            <Button
              disableRipple
              isIconOnly
              className="overflow-visible"
              radius="full"
              variant="light"
              aria-label={t('aria.notifications')}
            >
              {/* @ts-ignore */}
              {notifications?.objects?.filter((n: Notification) => !n.was_read).length > 0 ?
              //@ts-ignore
                <Badge color="danger" className="bg-[#C20E4C] w-[16px] top-[3px] right-[5px] h-[16px] text-[10px] leading-[16px] font-regular" content={notifications?.objects?.filter((n: any) => !n.was_read).length} showOutline={false} size="xs" aria-label="3 новых уведомления">
                  <Icon className="text-default-500" icon="solar:bell-outline" width={24} height={24} aria-label={t('aria.notifications')} />
                </Badge>
              :
                <Icon className="text-default-500" icon="solar:bell-outline" width={24} height={24} aria-label={t('aria.notifications')} />
              }
            </Button>
          </PopoverTrigger>
          <PopoverContent className="max-w-[90vw] p-0 sm:max-w-[439px]">
            <NotificationsCard className="w-full shadow-none" />
          </PopoverContent>
        </Popover>
        {/* User Menu */}
        <Dropdown showArrow placement="bottom-start" aria-label={t('aria.userMenu')}>
          <DropdownTrigger>
            <Button disableRipple isIconOnly className="-mr-1" radius="full" variant="light" aria-label={t('aria.userMenu')}>
              <div className="border border-default-300 rounded-full p-[2px]">
              <Avatar
                className="h-[28px] w-[28px] cursor-pointer"
                name={getInitials(user?.name)}
                src={user?.avatar ?? undefined}
                aria-label={`${t('aria.userProfile')} ${user?.name}`}
              />
              </div>

            </Button>
          </DropdownTrigger>
          <DropdownMenu aria-label="Custom item styles" disabledKeys={["profile"]}>
            <DropdownSection  aria-label="Profile & Actions">
              <DropdownItem
                key="profile"
                isReadOnly
                className="h-14 gap-2 opacity-100"
                textValue="Signed in as"
              >
                <User
                  avatarProps={{
                    size: "sm",
                    imgProps: {
                      className: "transition-none",
                    },
                    src: user?.avatar ?? undefined,
                    name:getInitials(user?.name)
                  }}
                  classNames={{
                    name: "text-default-600",
                    description: "text-default-500",
                  }}
                  // description={user?.tg_tag}
                  name={user?.name}
                  aria-label={`Информация о пользователе ${user?.name}`}
                />
              </DropdownItem>
              {/* <>
                {accounts?.length > 0 && accounts?.slice(0, 5).map((item:any) => (
                  <DropdownItem 
                    key={item?.of_user_id}
                    className="opacity-100 py-[0px]"
                    textValue="Signed in as"
                  >
                    <AccountItem item={item}/>
                  </DropdownItem>
                ))}
              </>
              <DropdownItem onClick={() => navigate('/client/accounts')} endContent={React.useMemo(() => (
                <SidebarChip ariaLabel={`${accountCount} аккаунтов`}>
                  {accountCount}
                </SidebarChip>
              ), [accountCount])} key="dashboard">{t('sidebar.allAccounts')}</DropdownItem> */}


              <DropdownItem onClick={() => navigate('/client/account/wallet')} endContent={<WalletChip />} key="settings">{t('sidebar.balance')}</DropdownItem>
            </DropdownSection>
            <DropdownSection aria-label="Help & Feedback">
              <DropdownItem onClick={() => navigate('/client/account/settings')} key="help_and_feedback">{t('sidebar.settings')}</DropdownItem>
              {/* //@ts-ignore */}
              <DropdownItem key="logout" onClick={() => logout()}>{t('sidebar.logout')}</DropdownItem>
            </DropdownSection>
          </DropdownMenu>
        </Dropdown>
      </div>
    </div>
  );
});
const AccountItem:FC<any> = ({item}) => {
  return(
    <a href={`/client/accounts/${item?.of_user_id}`} style={{cursor:'pointer'}} className="flex items-center gap-[8px] py-[6px]">
      <img src={item?.avatar} className="rounded-full w-[16px] h-[16px]"/>
      <Typography variant="body3">
        @{item?.of_user_name}
      </Typography>
    </a>
  )
}
SidebarHeader.displayName = "SidebarHeader";

// Основной компонент сайдбара
const Component = React.memo(() => {
  const { t } = useTranslations();
  return (
    <div className="h-full min-h-[48rem]">
      <div className="relative flex h-full w-[291px] flex-1 flex-col border-r-small border-divider px-[16px] py-[24px]">
        <SidebarHeader />
        <div className="-mr-6 h-full max-h-full py-6 pr-6" aria-label={t('aria.navigationMenu')}>
          <SidebarActiveTracker />
          <Spacer y={8} />
        </div>
      </div>
    </div>
  );
});

Component.displayName = "Component";

export default Component;
